import EditIcon from "@mui/icons-material/Edit";
import { Box } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { ReactNode } from "react";
import { type DropdownItem, SystemDropdown } from "../Inputs/SystemDropDown";
import { EDITABLE_CELL_PENCIL_SIZE, EDITABLE_CELL_REVEAL_SX } from "./editableCellSx";

type CommonProps<T extends string> = {
	/** The choices. A `disabled` item with a `secondaryLabel` says why it can't be picked from here. */
	items: DropdownItem<T>[];
	/** What the cell shows at rest — usually the same chip / mark the read-only column drew. */
	display: ReactNode;
	/** The column's name — the title of the phone bottom sheet the choices open in. */
	label: string;
	/** Read-only: the display alone, with no pencil and nothing to open. */
	disabled?: boolean;
	dataTestId: string;
};

/** One value: a pick commits and closes. */
type SingleProps<T extends string> = CommonProps<T> & {
	multiple?: false;
	value: T;
	/** Called only with a value different from `value`. */
	onChange: (next: T) => void;
};

/**
 * A set of values (a job's posting channels): ticks are held in the panel and committed once, on Done — so a
 * save per tick never happens. An empty set is a real answer ("none"), never "all".
 */
type MultipleProps<T extends string> = CommonProps<T> & {
	multiple: true;
	value: T[];
	/** Called only with a set different from `value` (order ignored), in `items` order. */
	onChange: (next: T[]) => void;
};

type Props<T extends string> = SingleProps<T> | MultipleProps<T>;

/** Same members, whatever the order. */
const sameSet = <T,>(a: readonly T[], b: readonly T[]): boolean => a.length === b.length && a.every((value) => b.includes(value));

const CENTERED_SX = { display: "flex", alignItems: "center", width: "100%", height: "100%", minWidth: 0 } as const;

/**
 * Strip the dropdown's field chrome so the closed control IS the cell's display: no outline (at rest, on hover
 * or open), no chevron — the hover pencil is the only affordance, as on the grid's editable text cells. Keyboard
 * focus still gets a ring, since the outline that used to show it is gone.
 */
const CELL_SELECT_SX = {
	height: "100%",
	"& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
	"& .MuiSelect-select": { p: 0, paddingRight: "0 !important", minHeight: 0, height: "100%", display: "flex", alignItems: "center" },
	"& .MuiSelect-select:focus-visible": { outline: (theme: Theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2, borderRadius: 0.5 },
	"& .MuiSelect-icon": { display: "none" },
} as const;

/**
 * A `SystemDataGrid` cell edited by picking from a short list — the select sibling of `EditableCell`. At rest
 * it shows the caller's own display node (a status chip, a step chip, a source mark); hovering or focusing the
 * cell fades in the same pencil, and clicking anywhere in the cell opens the choices (a bottom sheet on a
 * phone). The pick is reported through `onChange`; saving it is the caller's job (typically the same function
 * the grid uses as `processRowUpdate`, so a select and a text edit share one commit path). `multiple` edits a
 * set instead: the ticks are committed once, on the panel's Done.
 */
export function EditableSelectCell<T extends string>(props: Props<T>) {
	const { items, display, label, disabled, dataTestId } = props;
	if (disabled) return <Box sx={CENTERED_SX}>{display}</Box>;
	const renderValue = () => (
		<Box sx={{ ...CENTERED_SX, gap: 0.5 }}>
			<Box sx={{ minWidth: 0, overflow: "hidden" }}>{display}</Box>
			<EditIcon aria-hidden sx={{ flexShrink: 0, fontSize: EDITABLE_CELL_PENCIL_SIZE, ...EDITABLE_CELL_REVEAL_SX }} />
		</Box>
	);
	const shared = {
		items,
		renderValue,
		fieldLabel: label,
		fullWidth: true,
		formControlSx: { height: "100%", justifyContent: "center" },
		selectSx: CELL_SELECT_SX,
		"data-testid": dataTestId,
	} as const;
	if (props.multiple) {
		const { value, onChange } = props;
		return (
			<SystemDropdown<DropdownItem<T>>
				{...shared}
				multiple
				selectedItems={value}
				onChangeMultiple={(next) => {
					if (!sameSet(next, value)) onChange(next);
				}}
				// Ticks wait for Done, so one edit is one save — and an outside click discards them.
				commit="apply"
				// Empty means "none" here, not "all": no Select-all row.
				allOption=""
				// The display paints the empty set too (a dash), so the trigger renders it rather than nothing.
				displayEmpty
			/>
		);
	}
	const { value, onChange } = props;
	return (
		<SystemDropdown<DropdownItem<T>>
			{...shared}
			selectedItem={value}
			onChange={(next) => {
				if (next !== undefined && next !== value) onChange(next);
			}}
		/>
	);
}

export default EditableSelectCell;
